import { validatePathParams } from '@/utils/hooks/api/safePathParams';
import {
  buildApiEndpoint,
  extractBody,
  extractError,
} from '@/utils/hooks/api/typescript/methodHelpers';
import { useSnackBarContext } from '@/utils/snackbar';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { useEffect } from 'react';
import {
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';

export interface UseGetConfig {
  params?: Record<string, string | number | boolean>;
  pathParams?: Record<string, string | number | undefined>;
  pathParamValidators?: Record<string, RegExp>;
  fireOnMount?: boolean;
  skip?: boolean;
  refetchInterval?: number;
  keepPreviousData?: boolean;
  successMessage?: string;
  errorMessage?: string;
  revalidateInterval?: number;
  revalidateStrategy?: 'invalidate' | 'refetch';
}

export interface UseGetResult<T = unknown> {
  request: UseQueryResult<AxiosResponse<T>, AxiosError>;
  fire: () => Promise<UseQueryResult<AxiosResponse<T>, AxiosError>>;
  refetchWithNewParams: (
    params: Record<string, string | number | boolean>
  ) => Promise<void>;
  revalidateCache: () => Promise<void>;
  revalidateAndFetch: () => Promise<
    UseQueryResult<AxiosResponse<T>, AxiosError>
  >;
  isLoading: boolean;
  isError: boolean;
  data: T | undefined;
  error: unknown;
  isFetching: boolean;
}

export default function useGet<T = unknown>(
  endpoint: string,
  config: UseGetConfig = {}
): UseGetResult<T> {
  const { updateSnack } = useSnackBarContext();

  let baseEndpoint = endpoint;
  let pathParamsValid = true;

  if (config.pathParams) {
    const { valid, path } = validatePathParams(
      endpoint,
      config.pathParams,
      config.pathParamValidators
    );
    pathParamsValid = valid;
    if (valid && path) {
      baseEndpoint = path;
    }
  }

  let fullEndpoint = buildApiEndpoint(baseEndpoint);
  if (config.params) {
    const queryParams = Object.entries(config.params)
      .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
      .join('&');
    fullEndpoint = `${fullEndpoint}?${queryParams}`;
  }

  const query = useQuery<AxiosResponse<T>, AxiosError>(
    [fullEndpoint],
    () => axios.get<T>(fullEndpoint),
    {
      enabled: pathParamsValid && config.fireOnMount && !config.skip,
      refetchOnWindowFocus: false,
      refetchInterval: config.refetchInterval,
      keepPreviousData: config.keepPreviousData,
    }
  );

  const queryClient = useQueryClient();

  useEffect(() => {
    if (!query.isFetching) {
      if (config.successMessage && query.isSuccess) {
        updateSnack({ severity: 'success', message: config.successMessage });
      }
      if (config.errorMessage && query.isError) {
        updateSnack({ severity: 'error', message: config.errorMessage });
      }
    }
  }, [query.isFetching, query.isSuccess, query.isError]);

  const refetchWithNewParams = async (
    params: Record<string, string | number | boolean>
  ) => {
    let refetchEndpoint = buildApiEndpoint(baseEndpoint);
    if (params) {
      const queryParams = Object.entries(params)
        .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
        .join('&');
      refetchEndpoint = `${refetchEndpoint}?${queryParams}`;
    }
    return queryClient.refetchQueries([refetchEndpoint]);
  };

  const revalidateCache = async () => {
    await queryClient.invalidateQueries([fullEndpoint]);
  };

  const revalidateAndFetch = async () => {
    await queryClient.invalidateQueries([fullEndpoint]);
    if (query.isFetching) return query;
    return query.refetch();
  };

  useEffect(() => {
    if (!config.revalidateInterval) return;
    const interval = setInterval(() => {
      if (query.isFetching) return;
      if (config.revalidateStrategy === 'invalidate') {
        revalidateCache();
      } else {
        revalidateAndFetch();
      }
    }, config.revalidateInterval);
    return () => clearInterval(interval);
  }, [
    config.revalidateInterval,
    config.revalidateStrategy,
    fullEndpoint,
    query.isFetching,
  ]);

  return {
    request: query,
    fire: query.refetch,
    refetchWithNewParams,
    revalidateCache,
    revalidateAndFetch,
    isLoading: query.isLoading,
    isError: query.isError,
    data: extractBody<T>(query.data),
    error: extractError(query.error ?? undefined),
    isFetching: query.isFetching,
  };
}
